import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "../components/Toast";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import axios from "axios";
import AddonCard from "../components/addon-shop/AddonCard";
import UpgradeCard from "../components/addon-shop/UpgradeCard";
import PageHeader from "../components/PageHeader";
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../context/OrganizationContext';
import { addonMetadata } from '../utils/planHelpers';
import useAdsEntitlement from '../hooks/useAdsEntitlement';
import useUpgradeToPro from '../hooks/useUpgradeToPro';
import "../components/addon-shop/addon-shop.css";
import "./ContratarAddon.css";

/**
 * AQUÍ VIVÍA `ADDON_METADATA`: 140 líneas a mano, SOLO EN ESPAÑOL, con «Bot Adicional» y los
 * precios escritos (`price: 47`). Era la cuarta copia de los datos de plan — y los textos eran
 * además peso muerto, porque las tarjetas ya leían nombre/descripción/beneficios de i18n.
 * Retirada el 9-oct-2026: lo único que de verdad era de esta página es su PRESENTACIÓN.
 *
 * De dónde sale cada cosa ahora:
 *   · precio, icono y planes → `addonMetadata` (el catálogo único, con paridad contra
 *     `functions/config/addonMetadata.js` vía test);
 *   · nombre, descripción y beneficios → los diccionarios (`metadata.{id}.*`, tres idiomas);
 *   · el degradado de cada tarjeta y qué insignia lleva → la tabla de abajo, que es lo único
 *     que esta página decide.
 *
 * El ORDEN de la tienda es el orden de esta tabla. `addon_ads_studio` NO está a propósito
 * (TEMPORAL 22-ago-2026: oculto hasta terminar configuración y pruebas; se retira de la página
 * en lugar de marcarlo «Próximamente» porque en una página de precios un producto anunciado y
 * no comprable genera preguntas que aún no se pueden responder; `addonMetadata` lo conserva —
 * es lógica de negocio y hace falta al reactivar).
 */
const PRESENTACION = {
  addon_bot:           { gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  addon_seats:         { gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
  addon_numero:        { gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  // `includedInPro: false` documentado en el catálogo: desde la Fase 3g vale +1 agenda en los
  // dos planes, así que en Pro NO lleva la insignia de «incluido».
  addon_calendar:      { gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', includedInPro: false },
  // Sólo Starter: en Pro el e-commerce ya está incluido y el add-on no daba nada (C7).
  addon_ecommerce:     { gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', includedInPro: true },
  addon_mensajes:      { gradient: 'linear-gradient(135deg, #667eea 0%, #06beb6 100%)' },
  addon_procesamiento: { gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  addon_repos:         { gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
};

const ADDON_METADATA = Object.entries(PRESENTACION).map(([id, presentacion]) => {
  const m = addonMetadata[id];
  if (!m) throw new Error(`addon desconocido en la tienda: ${id} no está en addonMetadata`);
  return {
    id,
    price: m.pricePerUnit,            // el precio NUNCA se escribe aquí: sale del catálogo
    icon: m.icon,
    availableIn: m.requiresPlan,
    proOnly: !m.requiresPlan.includes('starter'),
    ...presentacion,
  };
});

const ContratarAddon = () => {
  const navigate = useNavigate();
  const { context, loading: contextLoading } = useOrganization();
  const [plan, setPlan] = useState("");
  const [searchParams] = useSearchParams();
  const [selectedAddon, setSelectedAddon] = useState(null); // Single selection

  // PRESELECCIÓN POR URL: `/addon?addon=addon_mensajes`.
  //
  // Es lo que hace útil el correo de cuota en vez de decorativo — el cliente
  // llega a la tarjeta que le importa y no a un catálogo de ocho donde tiene que
  // acordarse de cuál era. Sin esto, el enlace del email lleva a la tienda y ahí
  // se acaba la ayuda.
  //
  // Sólo preselecciona; NO compra. Y sólo si el add-on existe y está habilitado
  // para su plan, para que un enlace viejo o manipulado no seleccione algo que
  // la pantalla mostraría deshabilitado.
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [addonsPendingCancel, setAddonsPendingCancel] = useState({});
  // Preview del prorrateo del add-on seleccionado. `null` mientras no se ha
  // pedido; `{ cargando: true }` mientras llega.
  const [previewAddon, setPreviewAddon] = useState(null);
  const { t } = useTranslation('addons');
  const { t: tc } = useTranslation('common');
  const { hasAds } = useAdsEntitlement();
  const { prorrateo: montoProrrateo, calcularPreview, confirmarUpgrade } = useUpgradeToPro(context);

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    if (contextLoading) return;

    const obtenerPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          // Read from organization or personal document based on context
          let docRef;
          if (context.mode === 'organization' && context.orgId) {
            docRef = doc(db, "organizations", context.orgId);
          } else {
            docRef = doc(db, "clientes", user.uid);
          }
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Org docs use 'planId', personal docs use 'plan'
            const planData = data.planId || data.plan;
            // Normalizar plan - puede ser string u objeto
            let cleanedPlan = "";
            if (typeof planData === "string") {
              cleanedPlan = planData.toLowerCase().trim();
            } else if (planData && typeof planData === "object") {
              cleanedPlan = (planData.id || planData.plan || "").toLowerCase().trim();
            }
            setPlan(cleanedPlan);
            // Load pending cancellations
            setAddonsPendingCancel(data.addonsPendingCancel || {});
          }
        }
      } catch (err) {
        console.error("❌ Error obteniendo plan:", err);
      } finally {
        setLoading(false);
      }
    };
    obtenerPlan();
  }, [contextLoading, context.mode, context.orgId]);

  // Handle selection change
  const handleSelect = async (id) => {
    setSelectedAddon(id);
    setPreviewAddon(null);

    // Si selecciona upgrade_pro, calcular prorrateo (useUpgradeToPro, compartido con /billing)
    if (id === "upgrade_pro") {
      await calcularPreview();
      return;
    }
    if (addonsPendingCancel[id]) return;   // deshacer una baja no cobra prorrateo

    // Preview del add-on. Si falla NO se bloquea la compra: el cliente pierde
    // el detalle del monto, no la capacidad de contratar. Fallar cerrado aquí
    // convertiría una caída del preview en una venta perdida.
    setPreviewAddon({ cargando: true });
    const payload = { addons: [id] };
    if (context.mode === 'organization' && context.orgId) {
      payload.organizationId = context.orgId;
    }
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('[preview] fail-open: sin sesión al pedir el prorrateo.');
        return setPreviewAddon(null);
      }
      const idToken = await user.getIdToken();
      const { data } = await axios.post(
        "https://us-central1-sistema-bks.cloudfunctions.net/calcularProrrateoAddon",
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setPreviewAddon(data);
    } catch (error) {
      // EL FAIL-OPEN DEJA RASTRO, y no es cosmético: tal como estaba, un preview
      // roto y uno que nunca se pidió se veían EXACTAMENTE igual —tarjeta
      // marcada, sin monto, consola limpia—. Diagnosticarlo costó ir a los logs
      // de Cloud Run a buscar si la function se había llamado siquiera.
      //
      // `console.warn` y no `error`: no es un fallo del cliente ni rompe nada.
      // Pero tiene que VERSE.
      const detalle = error.response?.data;
      console.warn(
        `[preview] fail-open: se contrata sin mostrar el prorrateo de "${id}".`,
        {
          motivo: detalle?.error || error.message,
          code: detalle?.code,
          status: error.response?.status,
          organizationId: payload?.organizationId ?? '(personal)',
        }
      );
      setPreviewAddon({ fallo: detalle?.error || error.message });
    }
  };

  const handlePagar = async () => {
    if (!selectedAddon) {
      return toast.info(t('selectToContinue'));
    }

    const user = auth.currentUser;
    if (!user) return toast.error(t('loginRequired'));

    setProcesando(true);

    try {
      const idToken = await user.getIdToken();
      const authHeaders = { headers: { Authorization: `Bearer ${idToken}` } };

      // Caso especial: Upgrade to Pro (useUpgradeToPro, compartido con /billing —
      // maneja su propio toast de éxito/error y el waitForPlanSync)
      if (selectedAddon === "upgrade_pro") {
        await confirmarUpgrade();
        return;
      }

      // If addon has pending cancellation, undo it instead of re-purchasing
      if (addonsPendingCancel[selectedAddon]) {
        const undoPayload = { addons: [selectedAddon], undo: true };
        if (context.mode === 'organization' && context.orgId) {
          undoPayload.organizationId = context.orgId;
        }
        await axios.post(
          "https://us-central1-sistema-bks.cloudfunctions.net/cancelAddon",
          undoPayload,
          authHeaders
        );
        toast.success(t('addonRestored'));
        window.location.reload();
        return;
      }

      // Contratar add-on individual
      const payload = { addons: [selectedAddon] };
      if (context.mode === 'organization' && context.orgId) {
        payload.organizationId = context.orgId;
      }
      // El mismo instante que usó el preview: lo mostrado y lo cobrado se
      // prorratean desde el mismo segundo. Si no hubo preview, no se manda y
      // Stripe usa "ahora" — el comportamiento anterior.
      if (Number.isInteger(previewAddon?.prorationDate)) {
        payload.prorationDate = previewAddon.prorationDate;
      }
      await axios.post(
        "https://us-central1-sistema-bks.cloudfunctions.net/addAddon",
        payload,
        authHeaders
      );

      toast.success(t('addonPurchased'));
      window.location.reload();
    } catch (error) {
      console.error("❌ Error al procesar:", error);
      const serverMsg = error.response?.data?.error;
      toast.error(serverMsg || t('paymentError'));
    } finally {
      setProcesando(false);
    }
  };

  // Determinar qué addons mostrar según el plan
  const getAddonsForPlan = () => {
    const applyAdsStatus = (addon) => {
      if (addon.id === 'addon_ads_studio' && hasAds) {
        return { ...addon, badge: 'included', disabled: true };
      }
      return addon;
    };

    // HOY NINGUNA TARJETA LLEVA LA BANDERA: los dos add-ons que la usaban
    // —mensajes y procesamiento— se activaron el 4-sep-2026. El mecanismo se
    // queda porque su razón sigue vigente, no por inercia: el siguiente add-on
    // vuelve a pasar por el mismo hueco entre «el código está» y «el producto
    // existe en Stripe».
    //
    // Un add-on cuyo producto todavía no existe en Stripe se muestra pero NO se
    // puede comprar. La alternativa —ocultarlo— dejaría la tarjeta lista y sin
    // usar hasta que alguien se acordara de mostrarla; así se ve que viene, y en
    // cuanto se pegue el `price_…` en `pricingMap.js` sólo hay que quitar la
    // bandera. Sin esto, el cliente llega a `addAddon`, el `priceId` no existe y
    // recibe un «error interno».
    const aplicarPendiente = (addon) => (addon.pendienteEnStripe
      ? { ...addon, badge: 'coming-soon', disabled: true }
      : addon);

    if (plan === 'pro') {
      return ADDON_METADATA.map(addon => aplicarPendiente(applyAdsStatus({
        ...addon,
        badge: addonsPendingCancel[addon.id] ? 'canceling' : (addon.includedInPro ? 'included' : null),
        disabled: false
      })));
    } else if (plan === 'starter') {
      return ADDON_METADATA.map(addon => aplicarPendiente(applyAdsStatus({
        ...addon,
        badge: addonsPendingCancel[addon.id] ? 'canceling' : (addon.proOnly ? 'pro-only' : null),
        disabled: addon.proOnly
      })));
    }
    return [];
  };

  const addonsToShow = getAddonsForPlan();

  // Preselección por URL, una sola vez y sólo sobre lo que de verdad se puede
  // comprar: la tarjeta tiene que existir para este plan y no estar
  // deshabilitada. Un enlace viejo —o manipulado— no selecciona nada.
  //
  // EL DEFECTO QUE HUBO AQUÍ (4-sep-2026): hacía `setSelectedAddon` directo, sin
  // pasar por `handleSelect`. Cuando el preview se añadió a `handleSelect`, el
  // camino de la preselección se quedó sin él: la tarjeta salía marcada y el
  // monto no aparecía nunca. Y es JUSTO el camino que usa el enlace del correo,
  // o sea el mayoritario.
  //
  // Dos caminos que seleccionan una tarjeta y sólo uno hace el trabajo completo.
  // Ahora hay uno.
  const yaPreseleccionado = useRef(false);
  useEffect(() => {
    const pedido = searchParams.get('addon');
    if (!pedido || yaPreseleccionado.current || selectedAddon || !addonsToShow.length) return;
    const candidato = addonsToShow.find((a) => a.id === pedido && !a.disabled);
    if (!candidato) return;
    // La ref evita repetirlo: `handleSelect` cambia el estado y este efecto
    // volvería a entrar antes de que `selectedAddon` esté visible aquí.
    yaPreseleccionado.current = true;
    handleSelect(candidato.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, addonsToShow, selectedAddon]);

  return (
    <div className="addons-container">
      <PageHeader title={t('shopTitle')} />

      {loading ? (
        <p className="loading">{t('loadingPlan')}</p>
      ) : (
        <p className="plan-actual">{t('currentPlan')} <strong>{plan || t('notDefined')}</strong></p>
      )}

      {!loading && (
        <div className="addons-grid">
          {/* Upgrade Card - Solo para Starter */}
          {plan === 'starter' && (
            <UpgradeCard
              selected={selectedAddon === 'upgrade_pro'}
              onSelect={handleSelect}
              currentPlan={plan}
            />
          )}

          {/* Add-on Cards */}
          {addonsToShow.map(addon => (
            <AddonCard
              key={addon.id}
              id={addon.id}
              name={t(`metadata.${addon.id}.name`)}
              price={addon.price}
              icon={addon.icon}
              gradient={addon.gradient}
              description={t(`metadata.${addon.id}.description`)}
              benefits={t(`metadata.${addon.id}.benefits`, { returnObjects: true })}
              badge={addon.badge}
              disabled={addon.disabled}
              selected={selectedAddon === addon.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}

      {selectedAddon === 'upgrade_pro' && montoProrrateo && (
        <p className="prorrateo">
          {t('prorrateo', { amount: montoProrrateo.monto, currency: montoProrrateo.moneda })}
        </p>
      )}

      {/* Add-on: el cargo NO es hoy. `addAddon` usa `create_prorations`, así que
          se acumula para la factura siguiente. Decir las tres cosas —hoy nada,
          cuánto y cuándo, y el recurrente— es lo que esta pantalla aporta. */}
      {selectedAddon && selectedAddon !== 'upgrade_pro' && previewAddon && (
        <div className="prorrateo-addon">
          {previewAddon.cargando ? (
            <p className="prorrateo">{tc('loading')}</p>
          ) : previewAddon.fallo ? (
            // Se puede contratar igual —fail-open—, pero no se finge que el
            // monto es cero: se dice que no se pudo calcular.
            <p className="prorrateo-fallo">{t('preview.noDisponible')}</p>
          ) : (
            <>
              <p className="prorrateo-hoy">{t('preview.hoyNada')}</p>
              <p className="prorrateo">
                {t('preview.seAnade', {
                  amount: previewAddon.monto?.toFixed(2),
                  currency: (previewAddon.currency || 'usd').toUpperCase(),
                  fecha: previewAddon.proximaFactura
                    ? new Date(previewAddon.proximaFactura * 1000).toLocaleDateString()
                    : t('preview.proximaFactura'),
                })}
              </p>
              {previewAddon.montoRecurrente > 0 && (
                <p className="prorrateo-recurrente">
                  {t('preview.despues', {
                    amount: previewAddon.montoRecurrente?.toFixed(2),
                    currency: (previewAddon.currency || 'usd').toUpperCase(),
                  })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        onClick={handlePagar}
        className="btn-confirmar"
        disabled={!selectedAddon || procesando || previewAddon?.cargando}
      >
        {/* «Confirmar y pagar» sólo donde SE PAGA: el upgrade cobra al momento.
            Para un add-on no se cobra nada en este clic, y anunciarlo así era
            una promesa que el sistema no cumple. */}
        {procesando
          ? tc('processing')
          : (selectedAddon && selectedAddon !== 'upgrade_pro' ? t('addToPlan') : t('confirmAndPay'))}
      </button>
    </div>
  );
};

export default ContratarAddon;
